import React, { useEffect, useMemo, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  buildPreviousSeasonSalesByDate,
  fetchDashboardLedgerSummary,
  fetchDashboardStores,
  fetchPhaseDailySummary,
  fetchPhaseKpiSummary,
  fetchPreviousSeasonSales,
  getLedgerChartSalesBreakdown,
  getLedgerStoreIdsForLocation,
  isKpiPreviousSeasonEnabled,
} from "./dashboardBttApi";
import {
  buildBranchDailyKpiRows,
  DAILY_KPI_BRANCHES,
  normalizeDailyKpiResponse,
  normalizeDateKey,
} from "./kpiPerDayUtils";
import { kpiCategoryHoverPlugin } from "./kpiChartPlugins";
import useVietnamTodayKey from "./useVietnamTodayKey";
import "./KPIPerDayChart.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const parseQuantity = (value) => {
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity >= 0 ? quantity : 0;
};

const formatDate = (value) => {
  const dateKey = normalizeDateKey(value);
  if (!dateKey) return "Không xác định";

  const [year, month, day] = dateKey.split("-");
  return `${day}/${month}/${year}`;
};

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("vi-VN", { maximumFractionDigits: 0 });

const buildPhaseChartOptions = (branchPhases) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    kpiCategoryHover: {
      backgroundColor: "rgba(47, 111, 184, 0.08)",
    },
    legend: { position: "bottom" },
    tooltip: {
      callbacks: {
        title: (items) => {
          const phase = branchPhases[items[0]?.dataIndex];
          return phase
            ? `${phase.branchName} · Phase ${phase.phaseNumber}: ${formatDate(phase.startDate)} - ${formatDate(phase.endDate)}`
            : "";
        },
        label: (context) =>
          `${context.dataset.label}: ${formatQuantity(context.raw)}`,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        offset: true,
        color: "rgba(101, 128, 155, 0.2)",
        lineWidth: 1,
        drawTicks: false,
      },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: "Số lượng bánh" },
      ticks: { callback: (value) => formatQuantity(value) },
    },
  },
});

const KPI_BRANCH_NAMES = DAILY_KPI_BRANCHES.reduce((result, branch) => {
  result[branch.seasonBranchId] = branch.name;
  return result;
}, {});

export const mapPhaseResponse = (responseData) => {
  const rows = Array.isArray(responseData)
    ? responseData
    : Array.isArray(responseData?.data)
      ? responseData.data
    : [];

  return rows
    .map((item) => ({
      id: item.IDPhase,
      phaseNumber: Number(item.SoPhase),
      phaseName: item.TenPhase || `Phase ${item.SoPhase}`,
      seasonName: item.TenMuaVu || "Mùa vụ",
      branchId: Number(item.ChiNhanh),
      branchName:
        KPI_BRANCH_NAMES[Number(item.ChiNhanh)] ||
        `Chi nhánh ${item.ChiNhanh}`,
      startDate: normalizeDateKey(item.NgayBatDau),
      endDate: normalizeDateKey(item.NgayKetThuc),
      kpi: parseQuantity(item.KPI),
    }))
    .filter(
      (item) =>
        Number.isFinite(item.phaseNumber) &&
        Number.isFinite(item.branchId) &&
        item.startDate &&
        item.endDate &&
        item.startDate <= item.endDate
    )
    .sort(
      (first, second) =>
        first.startDate.localeCompare(second.startDate) ||
        first.phaseNumber - second.phaseNumber
    );
};

const KPIPerPharseChart = ({
  ngayBatDau,
  ngayKetThuc,
  chiNhanh = [],
}) => {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const todayKey = useVietnamTodayKey();
  const previousSeasonEnabled = isKpiPreviousSeasonEnabled();

  const startDate = normalizeDateKey(ngayBatDau);
  const endDate = normalizeDateKey(ngayKetThuc);
  const branchIds = (Array.isArray(chiNhanh) ? chiNhanh : [chiNhanh])
    .map(Number)
    .filter(Number.isFinite);
  const branchIdsKey = branchIds.join(",");

  useEffect(() => {
    const controller = new AbortController();

    const fetchPhaseData = async () => {
      const hasPartialDateRange = Boolean(startDate) !== Boolean(endDate);
      if (
        hasPartialDateRange ||
        (startDate && endDate && startDate > endDate)
      ) {
        setPhases([]);
        setError("Khoảng ngày lấy KPI theo phase không hợp lệ.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const requestBranchIds = branchIdsKey
          ? branchIdsKey.split(",").map(Number)
          : DAILY_KPI_BRANCHES.map((branch) => branch.seasonBranchId);
        const [result, storesResponse] = await Promise.all([
          fetchPhaseKpiSummary({
            branchIds: requestBranchIds,
            startDate,
            endDate,
            signal: controller.signal,
          }),
          fetchDashboardStores({ signal: controller.signal }).catch((storeError) => {
            if (storeError.name === "AbortError") throw storeError;
            console.warn("Could not load stores for phase KPI chart:", storeError);
            return [];
          }),
        ]);

        const rawPhases = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
            ? result.data
            : [];
        const mappedPhases = mapPhaseResponse(rawPhases);
        const stores = Array.isArray(storesResponse) ? storesResponse : [];
        const daNangBranchIds = new Set(
          DAILY_KPI_BRANCHES.filter(
            (branch) => branch.location === "danang"
          ).map((branch) => branch.seasonBranchId)
        );
        const daNangPhases = mappedPhases.filter((phase) =>
          daNangBranchIds.has(phase.branchId)
        );
        const daNangStartDates = daNangPhases
          .map((phase) => phase.startDate)
          .sort();
        const daNangEndDates = daNangPhases
          .map((phase) => phase.endDate)
          .sort();
        const [dailyResponse, previousSeasonResponse, actualEntries] =
          await Promise.all([
            fetchPhaseDailySummary({
              phaseRows: rawPhases,
              signal: controller.signal,
            }),
            previousSeasonEnabled && daNangPhases.length > 0
              ? fetchPreviousSeasonSales({
                  startDate: daNangStartDates[0],
                  endDate: daNangEndDates[daNangEndDates.length - 1],
                  signal: controller.signal,
                })
              : Promise.resolve({ success: true, data: [] }),
            Promise.all(
              requestBranchIds.map(async (branchId) => {
                const branch = DAILY_KPI_BRANCHES.find(
                  (item) => item.seasonBranchId === branchId
                );
                if (!branch) return [branchId, new Map()];

                const branchPhases = mappedPhases.filter(
                  (phase) => phase.branchId === branchId
                );
                if (branchPhases.length === 0) return [branchId, new Map()];
                const calculationStartDate = branchPhases
                  .map((phase) => phase.startDate)
                  .sort()[0];
                const sortedEndDates = branchPhases
                  .map((phase) => phase.endDate)
                  .sort();
                const calculationEndDate =
                  sortedEndDates[sortedEndDates.length - 1];

                const storeIds = getLedgerStoreIdsForLocation(
                  branch.location,
                  stores
                );
                const ledgerSummary = await fetchDashboardLedgerSummary({
                  storeIds,
                  startDate: calculationStartDate,
                  endDate: calculationEndDate,
                  signal: controller.signal,
                });
                return [
                  branchId,
                  new Map(
                    (Array.isArray(ledgerSummary?.summaryByDate)
                      ? ledgerSummary.summaryByDate
                      : []
                    ).map((item) => [
                      normalizeDateKey(item.NgayBan),
                      getLedgerChartSalesBreakdown(item).banhBan,
                    ])
                  ),
                ];
              })
            ),
          ]);
        const dailyKpiRows = normalizeDailyKpiResponse(dailyResponse);
        const actualByBranch = new Map(actualEntries);
        const previousSeasonSalesByDate =
          buildPreviousSeasonSalesByDate(previousSeasonResponse);
        const calculatedRowsByBranch = new Map();

        requestBranchIds.forEach((branchId) => {
          const branch = DAILY_KPI_BRANCHES.find(
            (item) => item.seasonBranchId === branchId
          );
          const branchPhases = mappedPhases.filter(
            (phase) => phase.branchId === branchId
          );
          if (!branch || branchPhases.length === 0) return;

          const calculationStartDate = branchPhases
            .map((phase) => phase.startDate)
            .sort()[0];
          const sortedCalculationEndDates = branchPhases
            .map((phase) => phase.endDate)
            .sort();
          const calculationEndDate =
            sortedCalculationEndDates[sortedCalculationEndDates.length - 1];
          calculatedRowsByBranch.set(
            branchId,
            buildBranchDailyKpiRows({
              branch,
              dailyKpiRows,
              actualByDate: actualByBranch.get(branchId) || new Map(),
              startDate: calculationStartDate,
              endDate: calculationEndDate,
              todayKey,
            })
          );
        });

        setPhases(
          mappedPhases.map((phase) => {
            const phaseDailyRows = (
              calculatedRowsByBranch.get(phase.branchId) || []
            ).filter(
              (row) => row.date >= phase.startDate && row.date <= phase.endDate
            );
            const hasConfiguredDailyKpi = dailyKpiRows.some(
              (row) =>
                Number(row.ChiNhanh) === phase.branchId &&
                Number(row.IDPhase) === Number(phase.id)
            );
            const achieved = phaseDailyRows.reduce(
              (total, row) => total + row.actual,
              0
            );
            const requiredKpi = hasConfiguredDailyKpi
              ? phaseDailyRows.reduce(
                  (total, row) => total + row.requiredKpi,
                  0
                )
              : phase.kpi;
            const previousSeason =
              previousSeasonEnabled && daNangBranchIds.has(phase.branchId)
              ? Array.from(previousSeasonSalesByDate.entries()).reduce(
                  (total, [date, quantity]) =>
                    date >= phase.startDate && date <= phase.endDate
                      ? total + quantity
                      : total,
                  0
                )
              : 0;
            return { ...phase, achieved, previousSeason, requiredKpi };
          })
        );
      } catch (requestError) {
        if (requestError.name === "AbortError") return;
        console.error("Error fetching KPI by phase:", requestError);
        setPhases([]);
        setError("Không thể tải dữ liệu KPI theo phase.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchPhaseData();
    return () => controller.abort();
  }, [
    startDate,
    endDate,
    branchIdsKey,
    previousSeasonEnabled,
    reloadKey,
    todayKey,
  ]);

  const phaseCharts = useMemo(
    () =>
      DAILY_KPI_BRANCHES.map((branch) => {
        const branchPhases = phases.filter(
          (phase) => phase.branchId === branch.seasonBranchId
        );
        return {
          branch,
          phases: branchPhases,
          requiredKpi: branchPhases.map((phase) => phase.requiredKpi),
        };
      }).filter((chart) => chart.phases.length > 0),
    [phases]
  );

  if (loading) {
    return (
      <section className="daily-kpi-chart daily-kpi-chart__state">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        Đang tải KPI theo phase...
      </section>
    );
  }

  if (error) {
    return (
      <section className="daily-kpi-chart daily-kpi-chart__state is-error">
        <strong>{error}</strong>
        <button
          type="button"
          onClick={() => setReloadKey((value) => value + 1)}
        >
          Thử lại
        </button>
      </section>
    );
  }

  if (phaseCharts.length === 0) {
    return (
      <section className="daily-kpi-chart daily-kpi-chart__state">
        Không có dữ liệu KPI theo phase.
      </section>
    );
  }

  return (
    <section className="daily-kpi-chart">
      <header className="daily-kpi-chart__header">
        <div>
          <span>Kế hoạch và thực tế</span>
          <h3>KPI theo phase</h3>
        </div>
        <p>{phaseCharts.length} chi nhánh</p>
      </header>

      <div className="daily-kpi-chart__branches">
        {phaseCharts.map(({ branch, phases: branchPhases, requiredKpi }) => {
          const totals = branchPhases.reduce(
            (result, phase, index) => ({
              original: result.original + phase.kpi,
              actual: result.actual + phase.achieved,
              previousSeason:
                result.previousSeason + phase.previousSeason,
              required: result.required + requiredKpi[index],
            }),
            { original: 0, actual: 0, previousSeason: 0, required: 0 }
          );
          const chartData = {
            labels: branchPhases.map((phase) => [
              `Phase ${phase.phaseNumber}`,
              `${formatDate(phase.startDate)} - ${formatDate(phase.endDate)}`,
            ]),
            datasets: [
              {
                label: "KPI gốc",
                data: branchPhases.map((phase) => phase.kpi),
                backgroundColor: "rgba(47, 111, 184, 0.72)",
                borderColor: "#2f6fb8",
                borderWidth: 1,
                borderRadius: 5,
              },
              {
                label: "Thực tế đạt",
                data: branchPhases.map((phase) => phase.achieved),
                backgroundColor: "rgba(29, 158, 111, 0.76)",
                borderColor: "#1d9e6f",
                borderWidth: 1,
                borderRadius: 5,
              },
              ...(previousSeasonEnabled && branch.location === "danang"
                ? [
                    {
                      label: "Số lượng mùa trước",
                      data: branchPhases.map(
                        (phase) => phase.previousSeason
                      ),
                      backgroundColor: "rgba(124, 91, 196, 0.72)",
                      borderColor: "#6842b8",
                      borderWidth: 1,
                      borderRadius: 5,
                    },
                  ]
                : []),
            ],
          };

          return (
            <article
              className="daily-kpi-chart__branch"
              key={branch.seasonBranchId}
            >
              <div className="daily-kpi-chart__branch-title">
                <div>
                  <span>Chi nhánh</span>
                  <h4>{branch.name}</h4>
                </div>
                <b>{branchPhases.length} phase</b>
              </div>

              <div
                className={`daily-kpi-chart__totals${
                  previousSeasonEnabled && branch.location === "danang"
                    ? " has-previous-season"
                    : ""
                }`}
              >
                <div className="is-original">
                  <span>KPI gốc</span>
                  <strong>{formatQuantity(totals.original)}</strong>
                </div>
                <div className="is-actual">
                  <span>Thực tế đạt</span>
                  <strong>{formatQuantity(totals.actual)}</strong>
                </div>
                {previousSeasonEnabled && branch.location === "danang" && (
                  <div className="is-previous-season">
                    <span>Số lượng mùa trước</span>
                    <strong>{formatQuantity(totals.previousSeason)}</strong>
                  </div>
                )}
                {/* 
                <div className="is-required">
                  <span>KPI cần đạt</span>
                  <strong>{formatQuantity(totals.required)}</strong>
                </div>
                  */}
              </div>

              <div className="daily-kpi-chart__scroll">
                <div
                  className="daily-kpi-chart__canvas"
                  style={{ minWidth: Math.max(720, branchPhases.length * 180) }}
                >
                  <Bar
                    data={chartData}
                    options={buildPhaseChartOptions(branchPhases)}
                    plugins={[kpiCategoryHoverPlugin]}
                  />
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <footer className="daily-kpi-chart__source">
        Mỗi chi nhánh hiển thị một biểu đồ riêng. KPI cần đạt được điều chỉnh
        động ở frontend từ KPI_Goc và Bánh bán (LoaiPhieu = 1) của dashboard
        ledger; KPI_ThucSu lưu trong API không được trộn vào biểu đồ. Phần thiếu
        hoặc dư của các ngày đã qua trong phase hiện tại theo múi giờ
        Asia/Ho_Chi_Minh được phân bổ ngay cho các ngày còn lại.
        {previousSeasonEnabled && (
          <> Riêng Đà Nẵng có thêm số lượng mùa trước, được cộng từ các ngày dương
          thuộc phase và đối chiếu cùng ngày âm qua API SaleTrungThuRrevYear.</>
        )}
      </footer>
    </section>
  );
};

export default KPIPerPharseChart;
